// 데스크탑 우측 상단 — 선택된 직원의 월 캘린더 (Phase 1-2 후속)
//   7×N 그리드, 각 셀: 날짜 + 색 점/원으로 출퇴근 상태 표시
//   날짜 클릭 → onSelectDate(ymd) 콜백 → 우측 하단 상세 갱신
//   주말 컬럼 색상 다름, 오늘 강조
import React, { useMemo } from 'react';
import { matchesAttendanceFilter, getOverrideStatus } from './utils';

// 상태별 색상 (예외 스케줄 2종 + 5색)
const COLOR = {
  absence: '#7e22ce',     // 휴무 — 예외 스케줄 (purple) — 출퇴근 달력과 동일 톤
  modified: '#1d4ed8',    // 시간변경 — 예외 스케줄 (blue)
  normal: '#0f766e',      // 정상 (teal)
  late: '#d97706',        // 지각 (amber)
  early: '#c2410c',       // 조퇴 (orange)
  missing_out: '#dc2626', // 퇴근미체크 (red)
  dayoff: '#94a3b8',      // 휴무 (slate)
  empty: 'var(--border)', // 데이터 없음
};

// 행이 어느 상태에 속하는지 분류 (override 최우선 > dayoff > missing_out > late > early > normal)
function classifyRow(row) {
  if (!row) return 'empty';
  const ov = getOverrideStatus(row);
  if (ov?.kind === 'absence') return 'absence';
  if (ov?.kind === 'modified') return 'modified';
  if (matchesAttendanceFilter(row, 'dayoff'))      return 'dayoff';
  if (matchesAttendanceFilter(row, 'missing_out')) return 'missing_out';
  if (matchesAttendanceFilter(row, 'late'))        return 'late';
  if (matchesAttendanceFilter(row, 'early'))       return 'early';
  if (matchesAttendanceFilter(row, 'normal'))      return 'normal';
  return 'empty';
}

/**
 * 우측 상단 캘린더 — Phase 1-2 후속 v2
 * 직원 미선택 시: 전체 출근 인원 수 (예: 3/12) 표시 / 색 없음
 * 직원 선택 시: 출근/퇴근 시간 둘 다 + 색 점, 클릭 시 onEditDay(row) 모달 열림
 * 공휴일: 빨간 배경 + 공휴일 이름 작게
 * Props:
 *   year, month        — 표시 월
 *   userGroup          — 선택된 직원 group (없으면 전체 모드)
 *   groupedByUser      — 전체 모드에서 인원 수 계산용
 *   selectedDate       — 'YYYY-MM-DD'
 *   onSelectDate(ymd)  — 날짜 클릭 (선택 표시)
 *   onEditDay(row)     — 직원 선택 + 날짜 클릭 시 수정 모달 열기
 *   todayYmd           — 오늘 강조
 *   holidays           — 공공 공휴일 Set (YYYY-MM-DD)
 */
// 연차/반차 음영 색 (leavemgmt TYPE_BADGE 보라 톤 재사용 — 새 색 없음)
const LEAVE_SHADE = 'rgba(147,51,234,0.16)';
// calendar_effect → 음영 레이어 스타일 (full=전체 / half_am=왼쪽 절반 / half_pm=오른쪽 절반)
function leaveShadeStyle(effect) {
  if (effect === 'full_day') return { left: 0, right: 0 };
  if (effect === 'half_am')  return { left: 0, width: '50%' };   // 오전 쉼 → 왼쪽
  if (effect === 'half_pm')  return { right: 0, width: '50%' };  // 오후 쉼 → 오른쪽
  return null;
}

export function AttendanceCalendarPanel({ year, month, userGroup, groupedByUser, selectedDate, onSelectDate, onEditDay, todayYmd, holidays, leaveByDate = {} }) {
  // 월별 일자 → row 매핑 (직원 선택된 경우)
  const dateMap = useMemo(() => {
    const m = new Map();
    if (userGroup?.rows) {
      for (const r of userGroup.rows) {
        if (r.date_ymd) m.set(r.date_ymd, r);
      }
    }
    return m;
  }, [userGroup]);

  // 직원 미선택 모드: 전체 인원 / 일자별 출근 인원 수 사전 계산
  const totalUsers = useMemo(() => Object.keys(groupedByUser || {}).length, [groupedByUser]);
  const dayPresentCount = useMemo(() => {
    const m = new Map();
    if (userGroup) return m; // 직원 선택 시 미사용
    Object.values(groupedByUser || {}).forEach((g) => {
      (g.rows || []).forEach((r) => {
        if (!r.date_ymd) return;
        if (!r.check_in) return;
        m.set(r.date_ymd, (m.get(r.date_ymd) || 0) + 1);
      });
    });
    return m;
  }, [groupedByUser, userGroup]);

  // 월 그리드 빌드 (1일이 무슨 요일인지 → 앞쪽 빈칸)
  const grid = useMemo(() => {
    const yyyy = Number(year), mm = Number(month);
    const firstDay = new Date(Date.UTC(yyyy, mm - 1, 1));
    const lastDate = new Date(Date.UTC(yyyy, mm, 0)).getUTCDate();
    const startWeekday = firstDay.getUTCDay(); // 0=일~6=토
    const cells = [];
    // 앞쪽 빈 셀
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    // 일자 셀
    for (let day = 1; day <= lastDate; day += 1) {
      const ymd = `${yyyy}-${String(mm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dow = new Date(Date.UTC(yyyy, mm - 1, day)).getUTCDay();
      cells.push({ day, ymd, dow });
    }
    // 뒤쪽 채우기 (7의 배수)
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  // 직원 미선택 시에도 캘린더 그대로 표시 (인원 수 모드)
  return (
    <div style={panelStyle}>
      <div style={titleStyle}>
        {year}년 {month}월{userGroup ? ` — ${userGroup.user.name}` : ''}
        {!userGroup && totalUsers > 0 && (
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
            전체 {totalUsers}명 (셀 숫자: 출근 인원 / 전체)
          </span>
        )}
      </div>
      {/* 요일 헤더 */}
      <div style={gridStyle}>
        {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
          <div key={`h-${d}`} style={{
            ...weekdayCellStyle,
            color: i === 0 ? '#dc2626' : (i === 6 ? '#1d4ed8' : 'var(--text-muted)'),
          }}>{d}</div>
        ))}
        {/* 일자 셀 */}
        {grid.map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} style={emptyCellStyle} />;
          const row = userGroup ? dateMap.get(cell.ymd) : null;
          const status = classifyRow(row);
          const isToday = cell.ymd === todayYmd;
          const isSelected = cell.ymd === selectedDate;
          const isHoliday = holidays?.has?.(cell.ymd);
          const dayColor = (cell.dow === 0 || isHoliday) ? '#dc2626' : (cell.dow === 6 ? '#1d4ed8' : 'var(--text)');
          // 배경: 공휴일 > 일요일 > 토요일 > 기본
          const baseBg = isHoliday
            ? 'rgba(239,68,68,0.08)'
            : (cell.dow === 0 ? 'rgba(239,68,68,0.04)' : (cell.dow === 6 ? 'rgba(29,78,216,0.04)' : 'transparent'));
          const presentCount = !userGroup ? (dayPresentCount.get(cell.ymd) || 0) : null;
          // 연차/반차 음영 — 직원 선택 시에만. 행이 없어도(전일 연차) 음영은 뜬다.
          const leaveShade = userGroup ? leaveShadeStyle(leaveByDate[cell.ymd]) : null;
          return (
            <div
              key={cell.ymd}
              onClick={() => {
                onSelectDate(cell.ymd);
                // 직원 선택 + 데이터 있을 때만 수정 모달
                if (userGroup && row && onEditDay) onEditDay(row);
              }}
              style={{
                ...dayCellStyle,
                position: 'relative',
                background: isSelected ? 'rgba(99,102,241,0.15)' : baseBg,
                border: isSelected ? '2px solid #6366f1' : (isToday ? '2px solid #f59e0b' : '1px solid var(--border)'),
                cursor: userGroup ? (row ? 'pointer' : 'default') : 'pointer',
              }}
            >
              {/* 연차/반차 음영 배경 레이어 — 색점·시간은 이 위(zIndex)로 그대로 보임 */}
              {leaveShade && (
                <div style={{ position: 'absolute', top: 0, bottom: 0, background: LEAVE_SHADE, pointerEvents: 'none', borderRadius: 6, zIndex: 0, ...leaveShade }} />
              )}
              <div style={{ position: 'relative', zIndex: 1, fontSize: 11, fontWeight: isToday ? 700 : 500, color: dayColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{cell.day}</span>
                {!userGroup && totalUsers > 0 && (
                  <span style={{ fontSize: 9, color: presentCount > 0 ? '#0f766e' : 'var(--text-muted)' }}>
                    {presentCount}/{totalUsers}
                  </span>
                )}
              </div>
              {/* 직원 선택 시: 색 점 + 출근·퇴근 시간 가로 한 줄 (음영 위 zIndex) */}
              {userGroup && row && (
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLOR[status], margin: '2px auto 0' }} />
                  {(row.check_in_hhmm || row.check_out_hhmm) && (
                    <div style={{ fontSize: 11, color: 'var(--text)', marginTop: 1, textAlign: 'center', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.check_in_hhmm || '-'}<span style={{ color: 'var(--text-muted)' }}>·</span>{row.check_out_hhmm || (row.check_in ? <span style={{ color: '#dc2626' }}>미체크</span> : '-')}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* 범례 */}
      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text-sub)', marginTop: 8, flexWrap: 'wrap' }}>
        {[
          { k: 'normal', l: '정상' }, { k: 'late', l: '지각' }, { k: 'early', l: '조퇴' },
          { k: 'missing_out', l: '미체크' }, { k: 'dayoff', l: '휴무' },
        ].map((x) => (
          <span key={x.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLOR[x.k] }} />
            {x.l}
          </span>
        ))}
        {/* 연차/반차 음영 범례 (직원 선택 시 의미 있음) */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 8, borderRadius: 2, background: LEAVE_SHADE }} />
          연차(전체)·반차(반쪽)
        </span>
      </div>
    </div>
  );
}

const panelStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 12, display: 'grid', gap: 8 };
const titleStyle = { fontSize: 13, fontWeight: 700, color: 'var(--text)' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 };
const weekdayCellStyle = { textAlign: 'center', fontSize: 11, fontWeight: 700, padding: '4px 0' };
// 셀 크기 고정 (height 46 일정) — 직원 미선택/선택 모두 동일 → 캘린더 전체 높이 변동 없음
const dayCellStyle = { borderRadius: 6, padding: 3, height: 46, minHeight: 46, maxHeight: 46, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'stretch', transition: 'background 0.1s', overflow: 'hidden' };
const emptyCellStyle = { height: 46, minHeight: 46, maxHeight: 46 };
